import React, { useRef, useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { SUPPORTED_LOCALES, LOCALE_ALIASES, saveLocale, changeLocale } from '../i18n';
import { SUPPORTED_SEO_LOCALES, SeoLocaleKey, stripLocalePrefix } from '../component/seo/constants';
import { LocaleSelectorMessages } from './LocaleSelector.messages';
import './Header.css';

/**
 * The only language switcher.
 *
 * The header used to carry its own inline copy of this menu. That copy changed the
 * language but never touched the URL, and because LocaleLayout makes the URL
 * authoritative for the locale on public routes, every selection made from the
 * header was immediately reverted to English. Signed-in routes are not wrapped in
 * LocaleLayout, which is why the same menu appeared to work there — the two trees
 * disagreed rather than the menu being intermittent. One implementation, used in
 * both places, is what stops that drifting apart again.
 */

function findSeoLocaleKey(i18nKey: string): SeoLocaleKey | null {
  const entry = (Object.entries(SUPPORTED_SEO_LOCALES) as [SeoLocaleKey, any][])
    .find(([, v]) => v.i18nKey === i18nKey);
  return entry ? entry[0] : null;
}

interface LocaleSelectorProps {
  /** Open the menu upwards. The footer instance sits at the bottom of the page. */
  dropUp?: boolean;
  /** Called when the menu opens. The header uses it to close the mobile nav. */
  onOpen?: () => void;
}

export const LocaleSelector: React.FC<LocaleSelectorProps> = ({ dropUp = false, onOpen }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState('');
  const [highlight, setHighlight] = useState(0);
  // Tracked in state rather than read during render: reading window.matchMedia in
  // the render body makes the output depend on the viewport, which is unknowable
  // when the page is prerendered. The effect settles it immediately after mount,
  // well before the menu it gates can be opened.
  const [isMobile, setIsMobile] = useState(false);
  const triggerRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // Both refs are consulted because on mobile the menu is portalled to
  // document.body, so it is not a descendant of the trigger.
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (menuRef.current?.contains(target) || triggerRef.current?.contains(target)) return;
      setOpen(false);
      setFilter('');
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // 768px matches the .header-mobile-toggle media query in Header.css.
  useEffect(() => {
    const query = window.matchMedia('(max-width: 768px)');
    const update = () => setIsMobile(query.matches);
    update();
    query.addEventListener('change', update);
    return () => query.removeEventListener('change', update);
  }, []);

  const filtered = Object.entries(SUPPORTED_LOCALES)
    .filter(([code, label]) => label.toLowerCase().includes(filter.toLowerCase()) || code.toLowerCase().includes(filter.toLowerCase()) || (LOCALE_ALIASES[code] || '').toLowerCase().includes(filter.toLowerCase()));

  const close = () => { setOpen(false); setFilter(''); setHighlight(0); };

  const selectLocale = (code: string) => {
    // changeLocale, not i18n.changeLanguage: catalogues load on demand, and switching
    // the language before its messages exist would flash the page through English.
    void changeLocale(code);
    saveLocale(code);
    close();

    // Locales that have a URL must move to it, or LocaleLayout — which treats the
    // URL as authoritative — will switch the page straight back.
    const seoKey = findSeoLocaleKey(code);
    const cleanPath = stripLocalePrefix(location.pathname);
    if (seoKey !== null && seoKey !== 'en') {
      const prefix = SUPPORTED_SEO_LOCALES[seoKey].urlPrefix;
      navigate(cleanPath === '/' ? prefix : `${prefix}${cleanPath}`);
    } else if (location.pathname !== cleanPath) {
      // Leaving a localized URL for one that has no prefix: drop the prefix, or the
      // old locale in the path would win over the choice just made.
      navigate(cleanPath);
    }
  };

  const menu = (
    <div
      ref={menuRef}
      className={`header-dropdown header-lang-dropdown${dropUp ? ' header-lang-dropdown--up' : ''}`}
      role="listbox"
    >
      <div style={{ padding: '4px 4px 6px' }}>
        <input
          type="text"
          className="header-lang-search"
          placeholder="Search..."
          value={filter}
          onChange={e => { setFilter(e.target.value); setHighlight(0); }}
          onKeyDown={e => {
            if (e.key === 'ArrowDown') { e.preventDefault(); setHighlight(i => Math.min(i + 1, filtered.length - 1)); }
            else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlight(i => Math.max(i - 1, 0)); }
            else if ((e.key === 'Enter' || e.key === ' ') && filtered[highlight]) { e.preventDefault(); selectLocale(filtered[highlight][0]); }
            else if (e.key === 'Escape') { close(); }
          }}
          autoFocus
        />
      </div>
      {filtered.map(([code, label], i) => (
        <button key={code} className={`header-dropdown-item${i === highlight ? ' highlighted' : ''}`} onClick={() => selectLocale(code)}
          onMouseEnter={() => setHighlight(i)} role="option" aria-selected={i === highlight}>
          {label}
        </button>
      ))}
    </div>
  );

  return (
    <div ref={triggerRef} style={{ position: 'relative' }}>
      <button
        className="header-nav-link"
        onClick={() => {
          const next = !open;
          setOpen(next);
          setFilter('');
          setHighlight(0);
          if (next) onOpen?.();
        }}
        aria-label={t(LocaleSelectorMessages.LABEL)}
        style={{ display: 'flex', alignItems: 'center', gap: 4 }}
      >
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M2 12h20M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z"/></svg>
        <span className="header-lang-label">{SUPPORTED_LOCALES[i18n.language] || 'EN'}</span>
      </button>
      {open && (isMobile ? createPortal(menu, document.body) : menu)}
    </div>
  );
};
